import { useEffect, useState } from "react";
import { ArrowDown, Search, X } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_PILLS = ["Professional", "Cinematic", "Studio", "Minimalist"];

export default function Hero({ query, setQuery, activeCategory, setActiveCategory }) {
  const [counts, setCounts] = useState({ total_prompts: null, total_categories: null });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/stats").then((r) => r.data).catch(() => null),
      api.get("/prompts/categories").then((r) => r.data.categories || []).catch(() => []),
    ]).then(([stats, cats]) => {
      if (cancelled) return;
      if (stats) setCounts(stats);
      // Prefer the default 4 if any exist in DB; otherwise fall back to first 4 alphabetically
      const set = new Set(cats.map((c) => c.toLowerCase()));
      const preferred = DEFAULT_PILLS.filter((p) => set.has(p.toLowerCase()));
      const rest = cats
        .filter((c) => !preferred.some((p) => p.toLowerCase() === c.toLowerCase()))
        .sort();
      const pills = [...preferred, ...rest].slice(0, 6);
      setCategories(pills);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const promptsDisplay =
    counts.total_prompts == null ? "—" : `${counts.total_prompts}`;
  const categoriesDisplay =
    counts.total_categories == null ? "—" : `${counts.total_categories}`;

  const togglePill = (c) => {
    setActiveCategory((prev) => (prev === c ? "" : c));
  };

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-white/10 grain"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-40 blur-[120px]"
        style={{ background: "radial-gradient(circle, #E0007A33 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 right-0 w-[520px] h-[520px] rounded-full opacity-30 blur-[120px]"
        style={{ background: "radial-gradient(circle, #00E5FF33 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-12 lg:py-14">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
          <span className="font-mono-label text-[#00E5FF]">
            LIVE · 100% FREE · UPDATED WEEKLY
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-[-0.04em] leading-[0.95] max-w-[15ch] fade-up"
        >
          Get{" "}
          <span className="italic text-white/80" style={{ fontWeight: 500 }}>
            Premium
          </span>{" "}
          AI Profile Photos for{" "}
          <span className="text-[#E0007A]">Free.</span>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-3 md:mt-4 text-base md:text-lg text-white/60 max-w-2xl leading-relaxed"
        >
          Copy pro-level prompts instantly and paste into <span className="text-white">Gemini</span>.
          A curated, always-growing library of the exact prompts high-end designers, founders and
          creators use to get magazine-grade portraits — no subscriptions, no watermarks.
        </p>

        {/* Search bar */}
        <div className="mt-4 md:mt-5 max-w-2xl" data-testid="hero-search-wrap">
          <label className="relative block">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="search"
              inputMode="search"
              data-testid="hero-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prompts or categories (e.g., Cinematic, Professional, Dual-Tone)..."
              className="w-full pl-11 pr-11 py-2.5 rounded-full bg-black/40 border border-white/15 focus:border-[#00E5FF] focus:outline-none text-white placeholder-white/35 text-sm md:text-base transition-colors"
            />
            {query && (
              <button
                type="button"
                data-testid="hero-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </label>

          {/* Quick-filter pills */}
          {categories.length > 0 && (
            <div
              data-testid="hero-filter-pills"
              className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
              style={{ scrollbarWidth: "none" }}
            >
              {categories.map((c) => {
                const active = activeCategory === c;
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`hero-pill-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => togglePill(c)}
                    className={`flex-shrink-0 font-mono-label px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "bg-white text-[#050505] border-white"
                        : "text-white/70 border-white/15 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
              {activeCategory && (
                <button
                  type="button"
                  data-testid="hero-pill-clear"
                  onClick={() => setActiveCategory("")}
                  className="flex-shrink-0 font-mono-label px-3 py-1.5 rounded-full text-[#E0007A] hover:text-white transition-colors"
                >
                  Clear ×
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-3">
          <a
            href="#categories"
            data-testid="hero-cta-browse"
            className="inline-flex items-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] text-white font-semibold px-5 py-2.5 glow-magenta transition-transform duration-200 hover:scale-[1.03]"
          >
            Browse the Library
            <ArrowDown className="h-4 w-4" />
          </a>
          <a
            href="https://gemini.google.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-gemini"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-[#00E5FF]/40 hover:text-[#00E5FF] text-white/80 font-medium px-5 py-2.5 transition-colors"
          >
            Open Gemini
          </a>
        </div>

        <div className="mt-6 md:mt-8 grid grid-cols-3 gap-4 md:gap-8 max-w-xl">
          <div data-testid="hero-stat-prompts">
            <div className="font-display text-2xl md:text-3xl text-white">{promptsDisplay}</div>
            <div className="font-mono-label mt-0.5 text-white/50">Hand-crafted prompts</div>
          </div>
          <div data-testid="hero-stat-categories">
            <div className="font-display text-2xl md:text-3xl text-white">{categoriesDisplay}</div>
            <div className="font-mono-label mt-0.5 text-white/50">Style categories</div>
          </div>
          <div data-testid="hero-stat-price">
            <div className="font-display text-2xl md:text-3xl text-white">0€</div>
            <div className="font-mono-label mt-0.5 text-white/50">Forever</div>
          </div>
        </div>
      </div>
    </section>
  );
}
